import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trophy, Building2, Search, Plus, ChevronLeft, ChevronRight, Filter, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import CreateMatchDialog from '../components/CreateMatchDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 25;

const SourceBadge = ({ source }) => {
  if (source === 'PFAP') {
    return (
      <Badge className="bg-amber-100 text-amber-800 border-amber-200 gap-1" data-testid="badge-source-pfap">
        <Trophy className="w-3 h-3" /> PFAP
      </Badge>
    );
  }
  return (
    <Badge className="bg-sky-100 text-sky-800 border-sky-200 gap-1" data-testid="badge-source-external">
      <Building2 className="w-3 h-3" /> External
    </Badge>
  );
};

const VerificationBadge = ({ status }) => {
  const map = {
    Verified: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    Pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    Rejected: 'bg-red-100 text-red-800 border-red-200',
  };
  return (
    <Badge className={`${map[status] || 'bg-gray-100 text-gray-700'} text-xs`}
           data-testid={`badge-verification-${status}`}>
      {status || 'Unknown'}
    </Badge>
  );
};

/**
 * Phase 4 — Slice C · Unified Matches Page.
 *
 * Single screen for PFAP + External matches. Filter / search / paginate.
 */
const MatchesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canCreate = user?.role === 'Admin' || user?.role === 'Staff';

  const [matches, setMatches] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [offset, setOffset] = useState(0);

  // Filters
  const [filters, setFilters] = useState({
    q: '', match_source: '', club_id: '', age_group: '',
    competition: '', verification_status: '',
  });

  const fetchMatches = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));
      const r = await axios.get(`${API}/matches-v3?${params.toString()}`);
      setMatches(r.data || []);
    } catch (e) {
      toast.error('Failed to load matches');
    } finally { setLoading(false); }
  }, [filters, offset]);

  useEffect(() => { fetchMatches(); }, [fetchMatches]);

  useEffect(() => {
    (async () => {
      try {
        const [ath, cl, cf] = await Promise.all([
          axios.get(`${API}/athletes`),
          axios.get(`${API}/clubs`),
          axios.get(`${API}/matches-v3/config`),
        ]);
        setAthletes(Array.isArray(ath.data) ? ath.data : (ath.data?.athletes || []));
        setClubs(cl.data || []);
        setConfig(cf.data || null);
      } catch (e) { /* non-fatal */ }
    })();
  }, []);

  const ageGroupOptions = useMemo(() => {
    if (!filters.club_id) return [];
    const c = clubs.find(x => x.id === filters.club_id);
    return c?.age_groups || [];
  }, [clubs, filters.club_id]);

  const resetFilters = () => {
    setFilters({ q: '', match_source: '', club_id: '', age_group: '', competition: '', verification_status: '' });
    setOffset(0);
  };

  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <Layout>
      <div className="p-6 lg:p-8 space-y-5" data-testid="matches-page">
        <div className="flex justify-between items-start gap-3 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100"
                style={{ fontFamily: 'Manrope, sans-serif' }}
                data-testid="matches-page-title">
              Matches
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Unified PFAP + external club match history.
            </p>
          </div>
          {canCreate && (
            <Button
              onClick={() => setCreateOpen(true)}
              className="bg-pfap-green hover:bg-pfap-green/90 text-white"
              data-testid="open-create-match"
            >
              <Plus className="w-4 h-4 mr-1" /> Create Match
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card data-testid="match-filters">
          <CardContent className="py-3">
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" />
                <Input value={filters.q}
                       onChange={e => { setFilters({ ...filters, q: e.target.value }); setOffset(0); }}
                       placeholder="Search opponent / competition"
                       className="pl-8 h-9"
                       data-testid="filter-q" />
              </div>
              <Select value={filters.match_source || 'All'}
                      onValueChange={(v) => { setFilters({ ...filters, match_source: v === 'All' ? '' : v }); setOffset(0); }}>
                <SelectTrigger className="w-[140px] h-9" data-testid="filter-source">
                  <SelectValue placeholder="Source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All sources</SelectItem>
                  <SelectItem value="PFAP">PFAP</SelectItem>
                  <SelectItem value="External">External</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.club_id || 'All'}
                      onValueChange={(v) => { setFilters({ ...filters, club_id: v === 'All' ? '' : v, age_group: '' }); setOffset(0); }}>
                <SelectTrigger className="w-[180px] h-9" data-testid="filter-club">
                  <SelectValue placeholder="Club" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All clubs</SelectItem>
                  {clubs.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {ageGroupOptions.length > 0 && (
                <Select value={filters.age_group || 'All'}
                        onValueChange={(v) => { setFilters({ ...filters, age_group: v === 'All' ? '' : v }); setOffset(0); }}>
                  <SelectTrigger className="w-[120px] h-9" data-testid="filter-age-group">
                    <SelectValue placeholder="Age Group" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All age groups</SelectItem>
                    {ageGroupOptions.map(ag => (
                      <SelectItem key={ag} value={ag}>{ag}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Select value={filters.verification_status || 'All'}
                      onValueChange={(v) => { setFilters({ ...filters, verification_status: v === 'All' ? '' : v }); setOffset(0); }}>
                <SelectTrigger className="w-[140px] h-9" data-testid="filter-verification">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All statuses</SelectItem>
                  <SelectItem value="Verified">Verified</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={resetFilters} data-testid="reset-filters">
                  <X className="w-4 h-4 mr-1" /> Reset
                </Button>
              )}
              {hasFilters && (
                <div className="text-xs text-gray-500 ml-auto flex items-center gap-1">
                  <Filter className="w-3 h-3" /> Filters active
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
          </div>
        ) : matches.length === 0 ? (
          <Card data-testid="matches-empty-state">
            <CardContent className="py-12 text-center text-gray-500">
              No matches found. {hasFilters && 'Try clearing filters.'}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="matches-list">
            {matches.map(m => (
              <Card key={m.id}
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(`/matches/${m.id}`)}
                    data-testid={`match-row-${m.id}`}>
                <CardContent className="py-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <SourceBadge source={m.match_source} />
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                          vs {m.opponent}
                          {m.result && <span className="ml-2 text-sm text-gray-500">({m.result})</span>}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {m.match_date} · {m.competition}
                          {m.club_name ? ` · ${m.club_name}` : ''}
                          {m.age_group ? ` · ${m.age_group}` : ''}
                          {m.venue ? ` · ${m.venue}` : ''}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <VerificationBadge status={m.verification_status} />
                      <Badge variant="outline" className="text-xs gap-1">
                        {m.participant_count || 0} player{(m.participant_count || 0) === 1 ? '' : 's'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        <div className="flex justify-center items-center gap-3 pt-2">
          <Button variant="outline" size="sm"
                  disabled={offset === 0 || loading}
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  data-testid="pagination-prev">
            <ChevronLeft className="w-4 h-4 mr-1" /> Prev
          </Button>
          <div className="text-xs text-gray-500" data-testid="pagination-info">
            Page {Math.floor(offset / PAGE_SIZE) + 1} · showing {matches.length} matches
          </div>
          <Button variant="outline" size="sm"
                  disabled={matches.length < PAGE_SIZE || loading}
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  data-testid="pagination-next">
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        <CreateMatchDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          athletes={athletes}
          clubs={clubs}
          config={config}
          onCreated={(m) => { setCreateOpen(false); navigate(`/matches/${m.id}`); }}
        />
      </div>
    </Layout>
  );
};

export default MatchesPage;
